import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pesnya-na-svadbu")!;

const faqItems = [
  { q: "Можно ли заказать песню на свадьбу за один день?", a: "Да. Демоверсия создаётся за 3 минуты. Даже если свадьба завтра — вы успеете." },
  { q: "Как добавить в песню нашу историю знакомства?", a: "Просто расскажите вашу историю: где познакомились, какие смешные моменты были. Чем больше деталей — тем точнее песня." },
  { q: "Какой стиль подходит для свадебной песни?", a: "Романтическая баллада — для первого танца, лёгкий поп — для весёлого поздравления, джаз — для элегантного банкета." },
  { q: "Можно ли включить песню на церемонии или банкете?", a: "Конечно. Полная версия скачивается в высоком качестве — файл можно передать ведущему или диджею." },
  { q: "Сколько стоит свадебная песня?", a: "Демоверсия бесплатна. Полная песня — от 249 ₽." },
];

const PesnyaNaSvadbu = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">Песня на свадьбу на заказ</h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Три часа ночи. Свадьба через неделю. Вы хотите что-то, от чего у них обоих перехватит дыхание.</p>
            <p>Вы рассказываете историю — как они познакомились в очереди за кофе, как он два месяца не решался написать. Через три минуты вы слышите <strong className="text-foreground">песню, в которой звучит их история</strong>.</p>
            <p><strong className="text-foreground">Персональная песня на свадьбу</strong> — не шаблон. А трек, который существует в единственном экземпляре — и написан про двух конкретных людей.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Почему песня — самый запоминающийся свадебный подарок</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>На свадьбах дарят посуду, технику, сертификаты. Через год молодожёны не вспомнят, от кого была мультиварка. Но песню с их историей — они будут слушать годами.</p>
              <p>Она идеальна как подарок от свидетелей, родителей или друзей. Подробнее — <Link to="/pesnya-v-podarok" className="text-primary hover:underline">песня в подарок</Link>.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">💍 Создать песню для свадьбы</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Готово за 3 минуты.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Когда включить песню на свадьбе</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground">Первый танец.</strong> Молодожёны выходят на паркет — и звучит их собственная песня.</p>
              <p><strong className="text-foreground">Поздравление свидетелей.</strong> Идеально сочетается с <Link to="/pesnya-pro-druga" className="text-primary hover:underline">песней про друга</Link>.</p>
              <p><strong className="text-foreground">Подарок от родителей.</strong> Бот превращает историю в нежную балладу.</p>
              <p><strong className="text-foreground">Сюрприз жениха невесте.</strong> О подарке для неё подробнее — <Link to="/pesnya-devushke-v-podarok" className="text-primary hover:underline">песня девушке в подарок</Link>.</p>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«Тамада замолчал. Весь зал слушал нашу песню»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Свидетель Андрей знал все истории друга. Написал всё в бот. Выбрал стиль — лёгкий рок с акустической гитарой. На банкете — когда зазвучала песня — друг обнял невесту и прошептал: «Это же про нас». Тамада стоял молча.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Подарить молодожёнам незабываемый момент</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Частые вопросы о свадебных песнях</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Пусть ваша любовь зазвучит</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Создайте песню, которая станет саундтреком вашей свадьбы.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PesnyaNaSvadbu;
